import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import type { GlossPlaidItemSummary } from "@bitwarden/web-vault/app/services/syncing/plaid/plaid.types";

export type PlaidItemContext = {
  items: GlossPlaidItemSummary[];
};

/**
 * Whether an active (non-errored) Plaid Item covers this vault account.
 * Matches via institution: account.c_institution.instoPlaidId === item.institution_id.
 */
export function hasActivePlaidItemCoveringAccount(
  isPremium: boolean,
  ctx: PlaidItemContext | undefined,
  accountId: string | undefined,
  accountViews: AccountView[] | null | undefined,
): boolean {
  if (!isPremium || ctx == null || !accountId || accountViews == null) {
    return false;
  }
  const account = accountViews.find((a) => a.id === accountId);
  if (!account || account.origin !== Origin.plaid) {
    return false;
  }
  const institutionId = account.c_institution?.instoPlaidId;
  if (!institutionId) {
    // No institution id means no Plaid Item can be matched to this account —
    // it is not covered. (Previously this fell through to "any healthy item".)
    return false;
  }
  return ctx.items.some(
    (item) => item.institution_id === institutionId && !item.requires_reauth && item.error == null,
  );
}

/**
 * Whether the Plaid Item covering this account requires re-authentication.
 */
export function requiresPlaidReauth(
  isPremium: boolean,
  ctx: PlaidItemContext | undefined,
  accountId: string | undefined,
  accountViews: AccountView[] | null | undefined,
): boolean {
  if (!isPremium || ctx == null || !accountId || accountViews == null) {
    return false;
  }
  const account = accountViews.find((a) => a.id === accountId);
  if (!account || account.origin !== Origin.plaid) {
    return false;
  }
  const institutionId = account.c_institution?.instoPlaidId;
  if (!institutionId) {
    return false;
  }
  return ctx.items.some(
    (item) => item.institution_id === institutionId && (item.requires_reauth || item.error != null),
  );
}

/**
 * Whether the Plaid Item for this account's institution has new accounts available to add.
 * Returns the matching item summary, or undefined if none.
 */
export function plaidItemWithNewAccounts(
  ctx: PlaidItemContext | undefined,
  institutionId: string | undefined,
): GlossPlaidItemSummary | undefined {
  if (!ctx || !institutionId) {
    return undefined;
  }
  return ctx.items.find(
    (item) => item.institution_id === institutionId && item.new_accounts_available,
  );
}
